import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Popover from 'components/shared/Popovers/Popover/Popover';

import ColorDot from './components/ColorDot';
import ColorButton from './components/ColorButton';
import ColorPickerPanel from './components/ColorPickerPanel';

/**
 * @param {import('./lib/propTypes').ColorPickerProps}
 */

const ColorPicker = ({
  color = '#0BA5EC',
  onChange = () => {},
  label = 'Color',
  className,
  labelClassName,
  buttonClassName,
  panelClassName,
  disabled,
  required,
  isDotPicker = false,
  panelPosition = 'right'
}) => {
  const [selectedColor, setSelectedColor] = useState(color);
  const popoverRef = useRef(null);

  useEffect(() => {
    setSelectedColor(color);
  }, [color]);

  const handleChange = (color, closePopover) => {
    setSelectedColor(color);
    onChange(color);
    closePopover();
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    popoverRef?.current?.buttonRef?.current?.click();
    popoverRef?.current?.buttonRef?.current?.focus();
  };

  // Blur the trigger after the popover closes so its focus-within outline
  // doesn't linger. Deferred to the next frame to run after Headless UI
  // restores focus to the trigger on close.
  const blurTrigger = useCallback(() => {
    requestAnimationFrame(() => popoverRef?.current?.buttonRef?.current?.blur());
  }, []);

  return (
    <div className={className}>
      {label && (
        <div className="flex min-h-[28px] items-center pb-1">
          <label
            className={cn(
              'text-sm font-500 first-letter:capitalize',
              disabled ? 'text-neutral-400' : 'text-neutral-800',
              labelClassName
            )}>
            {label}
            {required && <span className="text-danger-400">*</span>}
          </label>
        </div>
      )}
      <Popover
        isFixed
        position={panelPosition}
        ref={popoverRef}
        buttonClassName={cn(
          '!p-0 !min-w-0 !min-h-0 h-[40px]',
          !isDotPicker &&
            'border border-solid border-neutral-200 focus-within:border-primary-300 focus-within:!outline-[#CBF0FD] !outline !outline-2 !outline-transparent focus-within:!outline-2'
        )}
        panelClassName="!p-0 !rounded-[4px] border border-solid border-neutral-200 shadow-[0_0_16px_0_rgba(55,65,69,0.08)]"
        buttonTheme="transparent"
        dataQa="color-picker"
        disabled={disabled}
        icon={false}
        text={
          isDotPicker ? (
            <ColorDot
              selectedColor={selectedColor}
              disabled={disabled}
              buttonClassName={buttonClassName}
              onOpen={handleOpen}
            />
          ) : (
            <ColorButton
              selectedColor={selectedColor}
              disabled={disabled}
              buttonClassName={buttonClassName}
              onOpen={handleOpen}
            />
          )
        }>
        {({ closePopover }) => (
          <ColorPickerPanel
            selectedColor={selectedColor}
            panelClassName={panelClassName}
            onSelect={(recommendedColor) => handleChange(recommendedColor, closePopover)}
            onClose={blurTrigger}
          />
        )}
      </Popover>
    </div>
  );
};

export default ColorPicker;
